import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { AnyAction } from "redux";
import type { ThunkDispatch } from "redux-thunk";
import useAnalytics from "storefront/hooks/useAnalytics";
import { From } from "storefront/Analytics/EventCreators/productAddedToWishlist";
import { User, FollowedListings } from "storefront/User";
import {
  addToFittingRoom,
  removeFromFittingRoom,
} from "storefront/actions/fittingRoomActions";
import { GlobalState } from "storefront/GlobalState";
import { GrailedAPILightListing } from "storefront/Listing/GrailedAPILightListing";
import { AlgoliaListing } from "storefront/Listing/AlgoliaListing";
import { AlgoliaInstantSearchListing } from "storefront/components/FiltersInstantSearch/CustomInfiniteHits/NonEmpty";
import { ModuleType } from "storefront/Analytics/EventCreators/moduleClicked";
import { PageType } from "storefront/Analytics/Event";

const followedListingsSelector = (state: GlobalState): FollowedListings =>
  state.session.currentUser.followedListings || [];

type ValueAndSetter = [boolean, (user: User) => void];

const useIsFavorited = (
  listing:
    | AlgoliaInstantSearchListing
    | GrailedAPILightListing
    | AlgoliaListing,
  from: From | undefined,
  pageType: PageType | undefined,
  moduleType?: ModuleType,
  moduleName?: string,
): ValueAndSetter => {
  const dispatch =
    useDispatch<ThunkDispatch<GlobalState, undefined, AnyAction>>();
  const followedListings = useSelector(followedListingsSelector);
  const { track } = useAnalytics();
  const [isFavorited, setIsFavorited] = useState<boolean>(false);

  useEffect(() => {
    if (followedListings.includes(listing.id)) {
      setIsFavorited(true);
    }
  }, [followedListings, listing.id]);

  const toggleIsFavorited = (currentUser: User) => {
    if (isFavorited) {
      setIsFavorited(false);
      try {
        dispatch(removeFromFittingRoom(currentUser.id, listing, track, from));
      } catch {
        setIsFavorited(true);
      }
    } else {
      setIsFavorited(true);
      try {
        dispatch(
          addToFittingRoom(
            currentUser.id,
            track,
            listing,
            from,
            pageType,
            moduleType,
            moduleName,
          ),
        );
      } catch {
        setIsFavorited(false);
      }
    }
  };

  return [isFavorited, toggleIsFavorited];
};

export default useIsFavorited;
